import { CardBody, CardTitle } from '@arnold/common';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import { GraphQLErrorCard, Loading } from '../../components';
import {
  Features,
  OrganizationQuery,
  UserQuery,
  useLanguagesQuery,
  useOrganizationQuery,
  useSurveysQuery,
} from '../../generated/hooks';
import { useFeatureFlag } from '../../lib/helpers/hooks';
import { useGetOrganizationTeams, useGetSurveyGroups } from '../Surveys/utils';
import { OrganizationImport } from './OrganizationImport';

interface IProps {
  user: UserQuery['user'];
  organization: OrganizationQuery['organization'];
}

const UpdateTeamScreen = (props: IProps) => {
  const { t, i18n } = useTranslation('updateTeamsScreen');
  const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);

  const {
    organizationTeams,
    loading: teamsLoading,
    error: teamsError,
    refetch: teamsRefetch,
  } = useGetOrganizationTeams(props.organization?.id || '');

  const { surveyGroups, loading: surveyGroupsLoading, error: surveyGroupsError } = useGetSurveyGroups();

  const {
    loading: orgStructureLoading,
    error: orgStructureError,
    data: orgStructureData,
    refetch: orgStructureRefetch,
  } = useOrganizationQuery({
    variables: { organizationId: props.organization?.id || '' },
    skip: !props.user || !props.organization,
  });

  const {
    loading: languagesLoading,
    error: languagesError,
    data: languagesData,
  } = useLanguagesQuery({
    skip: !props.user || !props.organization,
  });

  const {
    data: surveysData,
    loading: surveysLoading,
    error: surveysError,
  } = useSurveysQuery({
    variables: {
      languageCode: i18n.language,
    },
  });

  const surveys = surveysData?.user.organization?.surveys;

  if (isEmployeeManagementDisabled) {
    return <Redirect to="/" />;
  }

  if (!props.user || !props.organization) {
    return (
      <Card>
        <CardBody>
          <CardTitle>{t('noOrganization')}</CardTitle>
        </CardBody>
      </Card>
    );
  }

  if (teamsError || surveyGroupsError || orgStructureError || languagesError || surveysError) {
    return (
      <GraphQLErrorCard
        error={teamsError || surveyGroupsError || orgStructureError || languagesError || surveysError}
      />
    );
  }

  if (
    teamsLoading ||
    surveyGroupsLoading ||
    orgStructureLoading ||
    languagesLoading ||
    surveysLoading ||
    !organizationTeams ||
    !surveyGroups ||
    !orgStructureData?.organization ||
    !languagesData ||
    !surveys
  ) {
    return <Loading />;
  }

  const { organization } = orgStructureData;

  return (
    <OrganizationImport
      organization={organization}
      organizationTeams={organizationTeams}
      surveyGroups={surveyGroups}
      refetch={() => {
        orgStructureRefetch();
        teamsRefetch();
      }}
      userId={props.user.id}
      languages={languagesData.languages}
      surveys={surveys}
    />
  );
};

export default UpdateTeamScreen;
